/* eslint-disable react/jsx-pascal-case */
/* eslint-disable react/no-unstable-nested-components */
import React, {
  useContext,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { MaterialReactTable, useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
  type MRT_RowSelectionState,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_GlobalFilterTextField,
  MRT_ToggleFiltersButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleGlobalFilterButton,
  MRT_Cell,
  MRT_TableOptions,
  MRT_TableInstance,
} from "material-react-table";
import { Box, IconButton, Button, MenuItem, TextField } from "@mui/material";
import {
  postMainAppApiData,
  getTaxReportsApiData,
  serializeTaxReportBody,
} from "services/api";
import { TFunction, useTranslation } from "react-i18next";
import { StoreContext } from "models/import";
import {
  FullMatchElement,
  MatchElement,
  ValidationDetail,
  ValidationTableFlow,
  ValidationTableMatchResult,
  Dictionary,
  CoinValue,
  ExchangeValue,
  ValidationSelectOption,
  ValidationMaterialTableProps,
  GeneralTransactionType,
  GeneralTransactionTypeModel,
  generalTransactionTypes,
  ValidationEditItem,
  ValidationTableDef,
  ValidationTableTransaction,
} from "models/validation";
import Tooltip, {
  TooltipAlignEnum,
  TooltipCursorEnum,
  TooltipModifier,
} from "components/tooltip";
import { TableDataRes } from "../base/tableBaseApi";
import {
  isTrade,
  getAccountBalance,
  getAccountBalanceAfter,
  getAccountBalanceByExchange,
  getAccountBalanceAfterByExchange,
  getTransactionType,
  getDateForValidation,
  getDateStringFromApi,
  getDateString,
  getExchanges,
  getBuy,
  getSell,
  getRowSelectionIds,
  getGeneralType,
  filterAllowedEditTransactionTypes,
  handleEditApiCall,
  getRowById,
  getSubRowItems,
  getCleanedSubRow,
  getStringValueForTrade,
  getFee,
  canExpand,
  getNotices,
  getSubRowPossibleMatches,
  getPossibleMatches,
  getRowId,
  hasWarnings,
  hasDuplicates,
  delay,
} from "services/transactions";
import ValidationEditDeleteComponent, {
  ValidationEditDeleteComponentProps,
} from "pages/validation/components/validationEditDelete";
import { useGetValidationRequest } from "pages/validation/network/validationRequest";
import { getStripedRowsForExpansion } from "services/util";
import { NumericFormat } from "react-number-format";
import { getDefaultMRTOptions } from "../materialTable";
import Pill, { PillModifier } from "components/pill";

interface ValidationDuplicateItem {
  duplicate_ids: number[];
  duplicate_count: number;
  transaction: ValidationTableTransaction
}

export default function ValidationDuplicatesTable(
  props: ValidationMaterialTableProps
) {
  const { t } = useTranslation();
  const { userSettings, themeName } = useContext(StoreContext);
  const tableRef = useRef();

  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [filters, setFilters] = useState(
    '{"currencies":0,"match_same_exchange":true,"amount_accuracy":90,"time_accuracy":2,"status":2}'
  );

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [deleteEditEnabled, setDeleteEnabled] = useState(true);

  const getSelectedRowItems = useCallback(() => {
    const selectedRowIds = getRowSelectionIds(rowSelection);
    const selectedRows = (data?.data || []).filter(
      (row: ValidationDuplicateItem) =>
        selectedRowIds.includes(row.transaction.id)
    );
    return selectedRows;
  }, [rowSelection, JSON.stringify(data?.data || [])]);

  const setRowSelectionAndUpdateProps = (
    rowSelection: MRT_RowSelectionState
  ) => {
    setRowSelection(rowSelection);
    props.setRowSelection(rowSelection);
  };

  const [warningsCurrentlyHidden, setWarningsCurrentlyHidden] = useState(false); //after calling the api we hide warnings until we change page
  const [duplicatesCurrentlyHidden, setDuplicatesCurrentlyHidden] =
    useState(false); //after calling the api we hide warnings until we change page
  const [fixDuplicatesEnabled, setFixDuplicatesEnabled] = useState(false); //button enabled or not

  const handleDuplicatesButtonPress = () => {
    if (tableRef.current) {
      tableRef.current.resetRowSelection();
      setRowSelectionAndUpdateProps({});
    }

    const encodedData = new URLSearchParams(`delete_duplicate_tx=true&is_react=true`);
    postMainAppApiData<any, any>(`duplicate_transactions.php`, encodedData)
      .then(() => {
        delay(1_000);// sleep 1 second so slave DB can update after the delete
        refetch();
      })
      .catch(() => {
        delay(1_000);// sleep 1 second so slave DB can update after the delete
        refetch();
      });
  };

  const toggleDeleteEditEnabled = () => {
    if (tableRef && tableRef.current) {
      tableRef.current.resetRowSelection();
      setRowSelectionAndUpdateProps({});
    }
    return;
  };

  const { data, isError, isFetching, isLoading, refetch } =
    useGetValidationRequest(
      "validationDuplicateTable",
      "/reports/tax/validation/duplicates",
      props.setIsFetching,
      columnFilters,
      props.globalSearch,
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
      filters,
      (
        response: TableDataRes<ValidationTableDef, ValidationDetail> | undefined
      ) => {
        setWarningsCurrentlyHidden(false); //reset hidden warnings on navigation
        setDuplicatesCurrentlyHidden(false);
        props.onLoadCb(response);
        setFixDuplicatesEnabled(hasDuplicates(response));
      }
    );
  const isDeletebuttonDisabled =
    isFetching || Object.keys(rowSelection).length < 1;

  // Edit
  const tableData = (data?.data || []);
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveRowEdits: MRT_TableOptions<ValidationDuplicateItem>["onEditingRowSave"] =
    async ({ exitEditingMode, row, values }) => {
      //we only support these fields for edit
      const valueItem: ValidationEditItem = {
        transaction_type: values.transaction_type,
        comment: values.comment,
      };

      const ids = [row.id];

      exitEditingMode();
      updateRows(ids, valueItem, false);
    };

  const updateRows = (
    id: string[],
    row: ValidationEditItem,
    fromToolbar: boolean
  ) => {
    const mapped = tableData.map((item) => {
      if (!id.includes(item.transaction.id || -1)) {
        return item;
      }

      const itemMapped = { ...item };

      if (row.transaction_type || row.transaction_type === "0") {
        itemMapped.transaction.transaction_type = row.transaction_type;
      }
      if (row.comment) {
        itemMapped.transaction.comment = row.comment;
      }

      return itemMapped;
    });
    //set the local data, we show the updated data immediately
    setTableData([...mapped]);

    if (fromToolbar) {
      toggleDeleteEditEnabled();
    }

    setIsSaving(true);

    handleEditApiCall(
      id,
      row,
      () => {
        setIsSaving(false);
        refetch();
      },
      () => {
        setIsSaving(false);
        refetch();
      }
    );
  };

  // Matches

  const deleteMatch = (
    pairId: number | null,
    transationId: number,
    userId: number
  ) => {
    const queryString: string = serializeTaxReportBody({
      method: "POST",
      path: "/reports/tax/validation/update-missing-pair",
      body: `{"user_id": ${userId}, "transaction_id": ${transationId}, "pair_id": ${pairId}}`,
      header: "Accept: application/json",
    });

    getTaxReportsApiData<any>(`/ajax/query.php?${queryString}`)
      .then((res: any) => {
        refetch();
      })
      .catch((e) => {
        refetch();
      });
  };

  const updateMatch = (
    pairId: number,
    transationId: number,
    userId: number
  ) => {
    const queryString: string = serializeTaxReportBody({
      method: "POST",
      path: "/reports/tax/validation/update-missing-pair",
      body: `{"user_id": ${userId}, "transaction_id": ${transationId}, "pair_id": ${pairId}}`,
      header: "Accept: application/json",
    });

    getTaxReportsApiData<any>(`/ajax/query.php?${queryString}`)
      .then((res: any) => {
        refetch();
      })
      .catch((e) => {
        refetch();
      });
  };

  const onDetailNavigate = (id: number | null): void => {
    if (!id) {
      return;
    }

    //We need to use jquery to fake a form submit with post
    $(
      '<form method="POST" target="_blank" action="/enter_coins.php"><input type="hidden" name="edit_tx" value="' +
        id +
        '" /></form>'
    )
      .appendTo("body")
      .submit()
      .remove();
  };

  const handleDelete = (item: number[] | null) => {
    if (!item) return;
    let params = ''
    for (let i in item) {
      params += `data[row_${item[i]}][DT_RowId]=row_${item[i]}&`
    }
    params.slice(0, -1);  //remove ending &
    const encodedData = new URLSearchParams(
      `action=remove&${params}`
    );

    if (tableRef && tableRef.current) {
      tableRef.current.resetRowSelection();
      setRowSelectionAndUpdateProps({});
    }

    postMainAppApiData<any>("ajax/tradelist_extended.php", encodedData)
      .then((res: any) => {
        refetch();
      })
      .catch((e) => {
        refetch();
      });
  };

  const columns = useMemo<MRT_ColumnDef<ValidationDuplicateItem>[]>(
    () => [
      {
        id: "duplicate_count",
        accessorKey: "duplicate_count",
        enableColumnFilter: false,
        enableEditing: false,
        header: t("# of Duplicates", { ns: "validation" }),
        accessorFn: (row) => row.duplicate_count,
        Cell: (info) => {
          const value: number | null =
            info.cell.row.original.duplicate_count || null;
          if (!value) return null;
          return (
            <span>
              <b>{value}</b>
            </span>
          );
        },
      },
      {
        id: "transaction_type",
        accessorKey: "transaction.transaction_type",
        enableColumnFilter: false,
        editVariant: "select",
        enableEditing: false,
        header: t("Type", { ns: "validation" }),
        accessorFn: (row) => row.transaction.transaction_type,
        Cell: (info) => {
          const stringValue = getTransactionType(
            info.row.original.transaction.transaction_type,
            info.row.original,
            props.transactionTypeSelectOption,
          );
          return (
            <span>
              <b>{stringValue}</b>
            </span>
          );
        },
      },
      {
        id: "sell",
        accessorKey: "transaction.sell",
        header: t("Out", { ns: "validation" }),
        accessorFn: (row) =>
          getSell(
            row.transaction.sell,
            row.transaction.sell_coin,
            row.transaction.sell_value_in_fiat,
            row.transaction.exchange,
            row
          ),
        Cell: ({ cell }) => (
          <Tooltip
            placement="bottom-start"
            arrow
            disableInteractive
            title={
              <>
                <b>
                  {cell.getValue<CoinValue>().value || ""}{" "}
                  {cell.getValue<CoinValue>().name || ""}{" "}
                </b>
                {cell.getValue<CoinValue>().fiatValue && (
                  <NumericFormat
                    value={cell.getValue<CoinValue>().fiatValue || ""}
                    decimalScale={2}
                    fixedDecimalScale
                    decimalSeparator={userSettings?.decimalCharacter}
                    thousandSeparator={userSettings?.thousandCharacter}
                    displayType="text"
                    renderText={(renderedValue: string) => (
                      <span style={{ fontSize: "11px" }}>
                        {`(${renderedValue} ${userSettings?.currency || ""})`}
                      </span>
                    )}
                  />
                )}
              </>
            }
          >
            <div
              className="ct_tableBase-text-wrap-one-line"
              style={{ maxWidth: "150px" }}
            >
              <b>
                {cell.getValue<CoinValue>().value || ""}{" "}
                {cell.getValue<CoinValue>().name || ""}{" "}
              </b>
              {cell.getValue<CoinValue>().fiatValue && (
                <NumericFormat
                  value={cell.getValue<CoinValue>().fiatValue || ""}
                  decimalScale={2}
                  fixedDecimalScale
                  decimalSeparator={userSettings?.decimalCharacter}
                  thousandSeparator={userSettings?.thousandCharacter}
                  displayType="text"
                  renderText={(renderedValue: string) => (
                    <span style={{ fontSize: "11px" }}>
                      {`(${renderedValue} ${userSettings?.currency || ""})`}
                    </span>
                  )}
                />
              )}
            </div>
          </Tooltip>
        ),
      },
      {
        id: "buy",
        accessorKey: "transaction.buy",
        header: t("In", { ns: "validation" }),
        accessorFn: (row) =>
          getBuy(
            row.transaction.buy,
            row.transaction.buy_coin,
            row.transaction.buy_value_in_fiat,
            row.transaction.exchange,
            row
          ),
        Cell: ({ cell }) => (
          <Tooltip
            placement="bottom-start"
            arrow
            disableInteractive
            title={
              <>
                <b>
                  {cell.getValue<CoinValue>().value || ""}{" "}
                  {cell.getValue<CoinValue>().name || ""}{" "}
                </b>
                {cell.getValue<CoinValue>().fiatValue && (
                  <NumericFormat
                    value={cell.getValue<CoinValue>().fiatValue || ""}
                    decimalScale={2}
                    fixedDecimalScale
                    decimalSeparator={userSettings?.decimalCharacter}
                    thousandSeparator={userSettings?.thousandCharacter}
                    displayType="text"
                    renderText={(renderedValue: string) => (
                      <span style={{ fontSize: "11px" }}>
                        {`(${renderedValue} ${userSettings?.currency || ""})`}
                      </span>
                    )}
                  />
                )}
              </>
            }
          >
            <div
              className="ct_tableBase-text-wrap-one-line"
              style={{ maxWidth: "150px" }}
            >
              <b>
                {cell.getValue<CoinValue>().value || ""}{" "}
                {cell.getValue<CoinValue>().name || ""}{" "}
              </b>
              {cell.getValue<CoinValue>().fiatValue && (
                <NumericFormat
                  value={cell.getValue<CoinValue>().fiatValue || ""}
                  decimalScale={2}
                  fixedDecimalScale
                  decimalSeparator={userSettings?.decimalCharacter}
                  thousandSeparator={userSettings?.thousandCharacter}
                  displayType="text"
                  renderText={(renderedValue: string) => (
                    <span style={{ fontSize: "11px" }}>
                      {`(${renderedValue} ${userSettings?.currency || ""})`}
                    </span>
                  )}
                />
              )}
            </div>
          </Tooltip>
        ),
      },
      {
        id: "exchange",
        accessorKey: "transaction.exchange",
        header: t("Account", { ns: "validation" }),
        enableEditing: false,
        accessorFn: (row) => row.transaction.exchange,
        Cell: (info) => {
          const exchangeValue: ExchangeValue | null = getExchanges(
            info.row.original.transaction.exchange,
            info.row.original
          );
          if (!exchangeValue) return null;

          return (
            <div
              className="ct_tableBase-text-wrap-one-line"
              style={{ maxWidth: "155px" }}
            >
              {exchangeValue.exchange1 || ""}
              {exchangeValue.exchange1 && exchangeValue.exchange2 && " -> "}
              {exchangeValue.exchange2 || ""}
            </div>
          );
        },
      },
      {
        id: "id",
        accessorKey: "transaction.id",
        enableColumnFilter: false,
        enableEditing: false,
        header: t("Tx ID", { ns: "validation" }),
        Cell: (info) => {
          const value: number | null = info.cell.row.original.transaction.id;
          if (!value) return null;
          return (
            <div
              className="ct_tableBase-text-wrap-one-line"
              style={{ maxWidth: "110px" }}
            >
              {value}
            </div>
          );
        },
      },
      {
        id: "transaction_time",
        accessorKey: "transaction.transaction_time",
        enableColumnFilter: false,
        enableEditing: false,
        header: t("Date", { ns: "validation" }),
        accessorFn: (row) =>
          getDateStringFromApi(row.transaction.transaction_time),
        Cell: (info) => {
          const value: string | null = getDateForValidation(
            info.cell.row.original.transaction.transaction_time,
            userSettings?.dateFormat,
            info.cell.row.original
          );
          if (!value) return null;
          return (
            <div
              className="ct_tableBase-text-wrap-one-line"
              style={{ maxWidth: "130px" }}
            >
              {value}
            </div>
          );
        },
      },
      {
        id: "duplicate_actions",
        accessorKey: "transaction.added_time",
        enableColumnFilter: false,
        enableEditing: false,
        header: '',
        accessorFn: (row) => getDateStringFromApi(row.transaction.added_time),
        Cell: (info) => {

          return (
            <div className="ct_validationStatusCell">
              <button
              key={`delete_all_${info.row.original.transaction.id}`}
              onClick={(e) => {
                e.stopPropagation();
                //avoid deleting the current transaction
                const duplicates = info.row.original.duplicate_ids.filter(id => id !== info.row.original.transaction.id);
                handleDelete(duplicates);
              }}
              type="button"
              className="ct_btn _link _old _noPadding _smallHeight"
            >
              <Pill
                key={`prev_${info.row.original.transaction.id}`}
                value={ t('Delete All', { ns: 'validation' }) }
                modifiers={[PillModifier.ERROR]}
              />
            </button>
            <button
              key={`delete_${info.row.original.transaction.id}`}
              onClick={(e) => {
                e.stopPropagation();
                const duplicates = info.row.original.duplicate_ids.filter(id => id !== info.row.original.transaction.id);
                if (duplicates.length < 1) return;
                handleDelete([duplicates.slice(-1)[0]]);
              }}
              type="button"
              className="ct_btn _link _old _noPadding _smallHeight"
            >
              <Pill
                key={`prev_${info.row.original.transaction.id}`}
                value={ t('Delete Last', { ns: 'validation' }) }
                modifiers={[PillModifier.ERROR]}
              />
            </button>
          </div>
          );
        },
      },
    ],
    [warningsCurrentlyHidden, duplicatesCurrentlyHidden, deleteEditEnabled] // deps for reload
  );

        const defaultMRTOptions = getDefaultMRTOptions<ValidationDuplicateItem>();

       const table = useMaterialReactTable({
      ...defaultMRTOptions,
      columns,
      data: tableData,
      initialState: {
        ...defaultMRTOptions.initialState,
        columnVisibility: {
            'mrt-row-select': false,
            'mrt-row-actions': false,
            before_balance: false,
            after_balance: false,
            before_balance_by_exchange: false,
            after_balance_by_exchange: false,
          },
      },
      muiTableBodyRowProps: (props) => ({
        ...defaultMRTOptions.muiTableBodyRowProps(props),
        onClick: (event: Event) => {
            if (isFetching) return;
            if (!canExpand(props.row.original)) return;
            event.stopPropagation();
            props.row.toggleExpanded();
        },
         sx: { 
            cursor: (!isFetching && canExpand(props.row.original)) ? 'pointer' : 'default',
            '& td': {
              backgroundColor: getStripedRowsForExpansion(props.row),
            },
         },
      }),
      muiToolbarAlertBannerProps: isError
        ? {
          color: 'error',
          children: t('Error loading data'),
        }
        : undefined,
      onColumnFiltersChange: setColumnFilters,
      onPaginationChange: (e) => {
          setRowSelectionAndUpdateProps({});
          setPagination(e);
        },
      onSortingChange: setSorting,
      onRowSelectionChange: (updater) => {
        setRowSelectionAndUpdateProps((prev) =>
          updater instanceof Function ? updater(prev) : updater,
        );
      },
      rowCount: data?.meta?.total_item_count ?? 0,
      defaultColumn: {
         ...defaultMRTOptions.defaultColumn,
        size: 70,
      },
      state: {
        ...defaultMRTOptions.state,
          columnFilters: columnFilters,
          globalFilter: props.globalSearch,
          isLoading,
          pagination,
          showAlertBanner: isError,
          showSkeletons: isFetching || isSaving || isLoading,
          sorting,
          rowSelection: rowSelection,
      },
      muiExpandButtonProps: ({
        row,
      }) => ({
        sx: {
          display: 'none', 
        },
      }),
      muiFilterTextFieldProps: {
        sx: { m: '0.5rem 0', width: '100%' },
      },
      getSubRows: (row) => undefined ,
      getRowId: (row, index, parent) => {
        if (parent) {
          return getRowId(row, index, parent.original, parent.index) 
        } else {
          return getRowId(row, index) 
        }
      },
      renderTopToolbarCustomActions: () => (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          { fixDuplicatesEnabled && (
              <Button
              color="error"
              disabled={isFetching || !fixDuplicatesEnabled}
              onClick={() => handleDuplicatesButtonPress()}
              variant="contained"
              sx={{ fontSize: '12px' }}
            >
              {t('Delete Duplicates', { ns: 'validation' })}
            </Button>
              )} 
            </div>
          ),
    });

    return (
      <>
      <MaterialReactTable table={table} />
      </>
    );

  }